import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { Comparator, CompareValidator, CompareValidatorParams } from "@cervval.js/common/src/services/validation.impl/validators/compare.validator";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";

import { is } from "@cervval.js/common/src/tools/object.tools";
import { HOURS_IN_ONE_DAY, MINUTES_IN_ONE_HOUR, SECONDS_IN_ONE_MINUTE, formatMinuteOrSecondsHHMMAndChooseOverflow, formatMinutesHHMM, formatSecondsHHMM, minutesToSeconds, parseHumanTimeString, secondsToMinutes } from "@cervval.js/common/src/tools/time.tools";
import { CjsTimePicker, TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT, TimePickerOptions } from "../../../pickers/timePicker/timePicker";
import { EditorWithPicker, EditorWithPickerOptions, defaultEditorWithPickerOptions } from "../editorWithPicker";
import { I18nType, i18n } from "./i18n/i18n";
import { TimeInRangesValidator } from "./timeEditor.validators";


export interface TimeEditorOptions extends EditorWithPickerOptions {

    /**
     * Enable "time" type for input and do not use default TimeEditor regex validators
     */
    enableTimeInputType?: boolean;
}

export const defaultTimeEditorOptions: TimeEditorOptions = {
    ...defaultEditorWithPickerOptions,
    buttonIcon: { name: "fa-clock" }
};

@CustomElement({
    name: "cjs-time-editor",
    i18n,
    elementCssClasses: ["cjs-editor"]
})
export class CjsTimeEditor extends EditorWithPicker<number, I18nType> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultTimeEditorOptions)
    public readonly options!: WebComponentOptions<TimeEditorOptions>;

    /** Shortcut to duration picker options */
    public get pickerOptions(): WebComponentOptions<TimePickerOptions> {
        return this._picker.options;
    }

    /**
     * The TimePicker used to edit the value of the input
     */
    protected _picker = new CjsTimePicker();

    constructor(options?: Partial<TimeEditorOptions>, datePickerOptions?: Partial<TimePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.pickerOptions.setWithoutRefresh(datePickerOptions);
    }

    /** When component refresh */
    public override refresh(): void {
        super.refresh();

        if (this.options.enableTimeInputType && (this.pickerOptions?.maxHoursLimit != null || this.pickerOptions?.maxHoursLimit && this.pickerOptions.maxHoursLimit <= TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT)) {
            // Force input type to time
            this.getInput().inputOptions.setAndRefreshIfChanged({ inputType: "time" });
        } else {// Reset to "text" if maxHoursLimit is upper than 23h
            this.getInput().inputOptions.setAndRefreshIfChanged({ inputType: "text" });
        }

    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsTimePicker {

        if (this._value != null) {
            this._picker.resetTimePickerView();
            this._picker.setValue(this._value);
            this._picker.refresh();
        } else {// Undefined, reset picker // TODO Create and replace with empty picker
            this._picker = new CjsTimePicker(this.pickerOptions);
        }

        return this._picker;
    }

    /**
     * Format the current time to a string which can be display for Humans
     * TODO :: Afficher le am/pm pour le mode am/pm de l'éditeur et ne plus mettre en mode 24h
     */
    public convertValueToInputValue(time: number | undefined): string {

        if (time != null) {
            if (this.options.enableTimeInputType) {
                return this.pickerOptions.useSeconds ? formatSecondsHHMM(time) : formatMinutesHHMM(time);
            }

            if ((this.pickerOptions.maxHoursLimit && this.pickerOptions.maxHoursLimit >= HOURS_IN_ONE_DAY) || this.pickerOptions.minNegativeHoursLimit) {
                return formatMinuteOrSecondsHHMMAndChooseOverflow(time, this.pickerOptions.useSeconds ?? false, { i18n: { dayLabel: this.i18n.labels.days } });
            }

            return this.pickerOptions.useSeconds ? formatSecondsHHMM(time) : formatMinutesHHMM(time);

        }
        return "";
    }

    /**
     * Format the displayed value in the input and return time from the formatted string
     * @returns the component value in minute
     */
    protected _convertInputValueToValue(inputValue: string | undefined): number | undefined {
        if (inputValue == null) {
            return undefined;
        }
        const value = parseHumanTimeString(inputValue, {
            acceptNegative: this.pickerOptions.minNegativeHoursLimit != null,
            acceptAboveH24: this.pickerOptions.maxHoursLimit != null && this.pickerOptions.maxHoursLimit > HOURS_IN_ONE_DAY
        });
        return value;
    }

    /**
     * Gets the value corresponding to the current input value
     * Converts value to seconds if useSeconds option set to true
     */
    protected _getValueFromInputComponent(): number | undefined {
        // Get value (in minutes)
        const value = this._convertInputValueToValue(this.getInput().components.inputText.value);
        if (!value) { return value; } // If value is 0, no need to check if useSeconds mode or not, we return 0 right away
        return this.pickerOptions.useSeconds ? minutesToSeconds(value) : value;
    }

    /**
     * Decorate the input depending on input value validity
     * @returns false if value is invalid
     */
    protected override _validateInputValue(value: number | undefined): boolean {
        if (!super._validateInputValue(value)) {
            return false;
        }
        return this._validateRanges(value);
    }

    /** Validate value in enabled ranges of timeEditor */
    protected _validateRanges(value?: number): boolean {
        let valueInMinutes = 0;
        if (value) {
            valueInMinutes = this.pickerOptions.useSeconds ? secondsToMinutes(value) : value;
        } else if (this._value) {
            valueInMinutes = this.pickerOptions.useSeconds ? secondsToMinutes(this._value) : this._value;
        }

        const rangesPropertyConfig: PropertyConfig = {
            propertyName: "ranges",
            label: "",
            typeUid: PropertyConfigType.NUMBER,
            validators: {
                ...validatorEntry(TimeInRangesValidator, { ranges: this.pickerOptions.ranges ?? [], useSeconds: this.pickerOptions.useSeconds })
            }
        };

        const minValue = this.pickerOptions.minNegativeHoursLimit ? this.pickerOptions.minNegativeHoursLimit * MINUTES_IN_ONE_HOUR : 0;
        const maxValue = (this.pickerOptions.maxHoursLimit ? this.pickerOptions.maxHoursLimit * MINUTES_IN_ONE_HOUR : TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT * MINUTES_IN_ONE_HOUR)
            + MINUTES_IN_ONE_HOUR - 1
            + (this.pickerOptions.useSeconds ? SECONDS_IN_ONE_MINUTE - 1 : 0);
        const maxPropertyConfig: PropertyConfig = {
            propertyName: "max",
            label: this.i18n.labels.value,
            typeUid: PropertyConfigType.NUMBER,
            validators: {
                ...validatorEntry(CompareValidator, is<CompareValidatorParams>({ comparator: Comparator.LESS_OR_EQUAL, value: maxValue, compareTime: true }))
            }
        };

        const minPropertyConfig: PropertyConfig = {
            propertyName: "min",
            label: this.i18n.labels.value,
            typeUid: PropertyConfigType.NUMBER,
            validators: {
                ...validatorEntry(CompareValidator, is<CompareValidatorParams>({ comparator: Comparator.GREATER_OR_EQUAL, value: minValue, compareTime: true }))
            }
        };

        const propsValues: Record<string, unknown> = {
            "ranges": value ?? this._value,
            "min": valueInMinutes,
            "max": valueInMinutes
        };
        const validatedProps: Record<string, PropertyConfig> = {
            "ranges": rangesPropertyConfig,
            "min": minPropertyConfig,
            "max": maxPropertyConfig
        };

        const errors = this._validation.validate(validatedProps, propsValues);

        this.setValidationError(errors["ranges"] ?? errors["min"] ?? errors["max"]);

        // Check if no error given === valid time
        return Object.keys(errors).length === 0;
    }
}